import type { CSSProperties, FC } from "react"
import type { GridChildComponentProps } from "react-window"
import React, { forwardRef, memo, useCallback, useMemo, useState } from "react"
import * as NucleoFillIcons from "nucleo-ui-fill-18"
import { IconMagnifierOutline18 } from "nucleo-ui-outline-18"
import { useDebounce } from "react-use"
import { areEqual, FixedSizeGrid as Grid } from "react-window"

import type { buttonVariantsType } from "@productlane/ui"
import { cn } from "@productlane/lib/src/cn"
import {
	iconSearchText,
	splitPascalCase,
} from "@productlane/lib/src/nucleo-icon-search"
import {
	Button,
	Empty,
	Input,
	NucleoFillIcon,
	Popover,
	ScrollArea,
	Tooltip,
} from "@productlane/ui"

import { EmptySearchIllustration } from "@/components/empty-search-illustration"

interface IconPickerProps {
	icon: string | null
	onChange: (icon: string | null) => void
	buttonVariant?: buttonVariantsType
	className?: string
	iconStyle?: CSSProperties
	disabled?: boolean
	buttonClassName?: string
	buttonStyle?: CSSProperties
	onMouseEnter?: (e: React.MouseEvent<HTMLButtonElement>) => void
	onMouseLeave?: (e: React.MouseEvent<HTMLButtonElement>) => void
}

const ICONS_PER_ROW = 10
const CELL_SIZE = 45.5
const GRID_HEIGHT = 300

interface IconEntry {
	name: string
	exportName: string
	label: string
	search: string
}

// nucleo-ui-fill-18 exports icons as `Icon<Name>Fill18`. We store the bare
// `<Name>` root so DB values stay short and mirror the lucide picker's format.
// The search index includes the space-split label, the concatenated lowercase
// root, and word-level synonyms so multi-keyword queries find more matches.
const ALL_ICONS: readonly IconEntry[] = Object.entries(NucleoFillIcons)
	.filter(([name, value]) => {
		if (!name.startsWith("Icon")) return false
		if (!name.endsWith("Fill18")) return false
		if (name === "Icon") return false
		return (
			typeof value === "function" ||
			(typeof value === "object" && value !== null && "$$typeof" in value)
		)
	})
	.map(([exportName]) => {
		const root = exportName.slice("Icon".length, -"Fill18".length)
		const label = splitPascalCase(root)
		const search = iconSearchText(root)
		return {
			name: root,
			exportName,
			label,
			search,
		}
	})

export const NucleoFillIconPicker = memo(
	forwardRef<HTMLDivElement, IconPickerProps>(
		(
			{
				icon,
				onChange,
				buttonVariant = "ghost",
				className,
				iconStyle,
				disabled = false,
				buttonClassName,
				buttonStyle,
				onMouseEnter,
				onMouseLeave,
			},
			ref,
		) => {
			const [searchTerm, setSearchTerm] = useState("")
			const [open, setOpen] = useState(false)
			const [debouncedIconSearch, setDebouncedIconSearch] = useState("")
			useDebounce(
				() => {
					setDebouncedIconSearch(searchTerm.trim().toLowerCase())
				},
				200,
				[searchTerm],
			)

			const filteredIcons = useMemo(() => {
				if (!debouncedIconSearch) return ALL_ICONS
				const tokens = debouncedIconSearch.split(/\s+/).filter(Boolean)
				if (tokens.length === 0) return ALL_ICONS
				return ALL_ICONS.filter((entry) =>
					tokens.every((token) => entry.search.includes(token)),
				)
			}, [debouncedIconSearch])

			const handleIconSelect = useCallback(
				(iconName: string) => {
					onChange(iconName)
					setOpen(false)
				},
				[onChange],
			)

			return (
				<Popover
					className="relative w-[30rem]"
					open={disabled ? false : open}
					onOpenChange={setOpen}
					ref={ref}
					content={
						<div>
							<Input
								name="icon-search"
								type="text"
								icon={IconMagnifierOutline18}
								variant="borderless"
								placeholder="Search icons"
								value={searchTerm}
								onChange={(e) => setSearchTerm(e.target.value)}
								className="mb-4"
								inputClassName="dark:!bg-gray-850"
								autoComplete="off"
							/>

							<div className="absolute left-0 right-0 border-t border-gray-300 dark:border-gray-700" />

							<ScrollArea className="mt-8 h-[300px] w-[448px]">
								{filteredIcons.length > 0 ? (
									<NucleoIconsGridVirtualizer
										key={debouncedIconSearch}
										filteredIcons={filteredIcons}
										onIconSelect={handleIconSelect}
									/>
								) : (
									<div className="flex h-full items-center justify-center text-gray-500">
										<Empty
											text="Try changing the search parameters"
											title="Nothing was found"
											image={<EmptySearchIllustration />}
										/>
									</div>
								)}
							</ScrollArea>
						</div>
					}
				>
					<Button
						variant={buttonVariant}
						className={cn("p-2", buttonClassName)}
						style={buttonStyle}
						disabled={disabled}
						onMouseEnter={onMouseEnter}
						onMouseLeave={onMouseLeave}
					>
						<NucleoFillIcon
							icon={icon}
							style={iconStyle}
							className={cn("size-4 min-h-4 min-w-4 opacity-90", className)}
						/>
					</Button>
				</Popover>
			)
		},
	),
)

interface CellData {
	icons: readonly IconEntry[]
	onSelect: (name: string) => void
}

const Cell = memo(
	({
		columnIndex,
		rowIndex,
		style,
		data,
	}: GridChildComponentProps<CellData>) => {
		const index = rowIndex * ICONS_PER_ROW + columnIndex
		const entry = data.icons[index]
		if (!entry) return null

		const Icon = NucleoFillIcons[
			entry.exportName as keyof typeof NucleoFillIcons
		] as React.FC<React.SVGProps<SVGSVGElement> & { size?: number }>

		return (
			<div style={style}>
				<Tooltip label={entry.label}>
					<Button
						variant="ghost"
						className="flex h-10 w-10 items-center justify-center rounded text-gray-700 hover:bg-gray-150 dark:text-gray-300 dark:hover:bg-gray-700/70"
						onClick={() => data.onSelect(entry.name)}
					>
						<Icon className="size-[18px]" />
					</Button>
				</Tooltip>
			</div>
		)
	},
	areEqual,
)

interface VirtualizerProps {
	filteredIcons: readonly IconEntry[]
	onIconSelect: (iconName: string) => void
}

const NucleoIconsGridVirtualizer: FC<VirtualizerProps> = memo(
	({ filteredIcons, onIconSelect }) => {
		const itemData = useMemo<CellData>(
			() => ({ icons: filteredIcons, onSelect: onIconSelect }),
			[filteredIcons, onIconSelect],
		)
		const rowCount = Math.ceil(filteredIcons.length / ICONS_PER_ROW)

		return (
			<Grid
				columnCount={ICONS_PER_ROW}
				columnWidth={CELL_SIZE}
				rowCount={rowCount}
				rowHeight={CELL_SIZE}
				height={GRID_HEIGHT}
				width={ICONS_PER_ROW * CELL_SIZE}
				itemData={itemData}
			>
				{Cell as never}
			</Grid>
		)
	},
)
